import React from 'react';
import {
    CheckCircleFilled,
    UserOutlined,
    CarOutlined,
    CreditCardOutlined,
    FileTextOutlined,
} from '@ant-design/icons';
import './CheckoutStepIndicator.css';

export type CheckoutStepId = 'identify' | 'delivery' | 'payment' | 'review';

export interface CheckoutStep {
    id: CheckoutStepId;
    icon: React.ReactNode;
    label: string;
}

const DEFAULT_STEPS: CheckoutStep[] = [
    { id: 'identify', icon: <UserOutlined />, label: 'Identificación' },
    { id: 'delivery', icon: <CarOutlined />, label: 'Entrega' },
    { id: 'payment', icon: <CreditCardOutlined />, label: 'Pago' },
    { id: 'review', icon: <FileTextOutlined />, label: 'Revisión' },
];

interface CheckoutStepIndicatorProps {
    currentStep: CheckoutStepId;
    completedSteps?: CheckoutStepId[];
    steps?: CheckoutStep[];
    onStepClick?: (stepId: CheckoutStepId) => void;
}

export const CheckoutStepIndicator: React.FC<CheckoutStepIndicatorProps> = ({
    currentStep,
    completedSteps = [],
    steps = DEFAULT_STEPS,
    onStepClick,
}) => {
    const currentIndex = steps.findIndex((s) => s.id === currentStep);

    const getStepStatus = (stepId: CheckoutStepId, index: number): 'completed' | 'active' | 'pending' => {
        if (completedSteps.includes(stepId)) return 'completed';
        if (stepId === currentStep) return 'active';
        if (index < currentIndex) return 'completed';
        return 'pending';
    };

    return (
        <div className="checkout-step-indicator" role="navigation" aria-label="Pasos del checkout">
            {steps.map((step, index) => {
                const status = getStepStatus(step.id, index);
                const isClickable = status === 'completed' && onStepClick;

                return (
                    <React.Fragment key={step.id}>
                        <div
                            className={`checkout-step-indicator__step checkout-step-indicator__step--${status}`}
                            onClick={isClickable ? () => onStepClick(step.id) : undefined}
                            role={isClickable ? 'button' : undefined}
                            tabIndex={isClickable ? 0 : undefined}
                            aria-current={status === 'active' ? 'step' : undefined}
                            aria-label={`${step.label}${status === 'completed' ? ' (completado)' : status === 'active' ? ' (actual)' : ' (pendiente)'}`}
                        >
                            <div className="checkout-step-indicator__icon">
                                {status === 'completed' ? (
                                    <CheckCircleFilled />
                                ) : (
                                    step.icon
                                )}
                            </div>
                            <span className="checkout-step-indicator__number">{index + 1}</span>
                        </div>
                        {index < steps.length - 1 && (
                            <div
                                className={`checkout-step-indicator__connector ${index < currentIndex || completedSteps.includes(steps[index + 1]?.id)
                                        ? 'checkout-step-indicator__connector--completed'
                                        : ''
                                    }`}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default CheckoutStepIndicator;
